import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {BehaviorSubject, Subject, pipe} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';

import DataSource from 'devextreme/data/data_source';
import {DxDataGridComponent} from 'devextreme-angular';
import {DxLookupComponent} from 'devextreme-angular';
import {DxTextBoxComponent} from 'devextreme-angular';
import {UntypedFormControl} from '@angular/forms';
import {ScreenService} from '../../../../../shared/services/general';
import {TestService} from '../../../../shared/services/test.service';
import {TreeTest} from '../../../../shared/models/order-entry/test';
import notify from 'devextreme/ui/notify';

@Component({
  selector: 'app-test-picker',
  templateUrl: './test-picker.component.html',
  styleUrls: ['./test-picker.component.scss'],
})
export class TestPickerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('dropDownGrid', {static: false}) public dropDownGrid?: DxDataGridComponent;
  @ViewChild('codeTextBox', {static: true}) public codeTextBox?: DxTextBoxComponent;
  @ViewChild('testLookup', {static: false}) public lookup?: DxLookupComponent;
  @ViewChild('container', {static: true}) public containerDiv?: ElementRef;
  @ViewChild('controls', {static: true}) public controlsDiv?: ElementRef;

  //@ts-ignore
  @Input() public pickerDataSource: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null);
  @Input() public allTestsInPicker: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  @Input() public testControl: UntypedFormControl = new UntypedFormControl();
  @Input() public disabled = true;

  public pickerSelectedKeys: number[] = [];
  public showAllTestsInPicker: boolean | null | undefined = false;
  public gridSize: number = -1;
  public isPhone: boolean = false;
  public tests: TreeTest[] = [];
  public selectedKeys: number[] = [];

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private testService: TestService,
    private screen: ScreenService,
    private elRef: ElementRef,
    private ref: ChangeDetectorRef,
  ) {
    this.textCodeChanged = this.textCodeChanged.bind(this);
    this.cleanLookup = this.cleanLookup.bind(this);
  }

  public ngAfterViewInit(): void {
    this.initResponsive();
    this.gridSize = this.GridSize;
    this.testControl.valueChanges.pipe(take(1))
      .subscribe(tests => {
        this.tests = tests;
      });
    this.ref.detectChanges();
  }

  public resizePickerDropDown() {
    if (
      this.elRef.nativeElement.querySelector('#testsPickerContainer') &&
      this.elRef.nativeElement.querySelector('#testsPickerContainer').parentElement &&
      this.elRef.nativeElement.querySelector('#testsPickerContainer').parentElement.parentElement
    ) {
      // @ts-ignore
      (this.elRef.nativeElement.querySelector('#testsPickerContainer') as HTMLElement).parentElement.parentElement.style.width = this.isPhone ? '250px' : '580px';
      // @ts-ignore
      (this.elRef.nativeElement.querySelector('#testsPickerContainer') as HTMLElement).parentElement.parentElement.style.height = '50vh';
      // @ts-ignore
      (this.elRef.nativeElement.querySelector('#testsPickerContainer') as HTMLElement).parentElement.style.height = '100%';
    }

    if (this.dropDownGrid !== undefined) {
      this.dropDownGrid.instance.clearFilter();
    }
  }

  public allTestsCheckboxValueChanged() {
    this.allTestsInPicker.next(this.showAllTestsInPicker ? this.showAllTestsInPicker : false);
  }

  public cleanLookup() {
    this.lookup?.instance.getDataSource().filter(null);
    this.lookup?.instance.getDataSource().load();
  }

  public textCodeChanged({value}: any, cb?: () => void) {
    if (value === '') {
      return;
    }
    this.cleanLookup();
    const dataSource = Object.create(this.pickerDataSource.value);
    dataSource.filter(['code', '=', value.toUpperCase()]);
    dataSource.load().then((v: TreeTest[]) => {
      this.cleanLookup();
      if (v.length === 0) {
        this.lookup?.instance.option('value', '');
        return notify(`Test with code ${this.codeTextBox?.value} not found`);
      }
      if (v && v.length === 1) {
        this.lookup?.instance.option('value', v[0].id);
        return cb && cb();
      } else {
        return notify(`More than one test with code ${this.codeTextBox?.value} matched`);
      }
    });
  }

  public addFoundTests() {
    const items = this.lookup?.instance.option('value');
    if (items) {
      this.testService.getTestWithChildren(typeof items === 'number' ? [items] : items)
        .pipe(take(1))
        .subscribe((tests: TreeTest[]) => {
          if (this.tests.length > 0) {
            for (const test of tests) {
              if (this.tests.findIndex(t => t.id === test.id) === -1) {
                this.tests.push(test);
              }
            }
          } else {
            this.tests.push(...tests);
          }
          this.testControl.setValue(this.tests);
          this.cleanLookup();
          this.lookup?.instance.option('value', null);
          if (this.codeTextBox) {
            this.codeTextBox.text = '';
          }

          this.ref.detectChanges();
        });
    }
  }

  public removeSelectedTest() {
    for (const key of this.selectedKeys) {
      const index = this.tests.findIndex(t => t.id === key);
      if (index !== -1) {
        const t = this.tests[index];
        this.tests.splice(index, 1);
        for (const child of t.children) {
          const childIndex = this.tests.findIndex(test => test.id === child.childId);
          if (childIndex !== -1) {
            this.tests.splice(childIndex, 1);
          }
        }
      }
    }
    this.selectedKeys = [];
    this.testControl.setValue(this.tests);
    this.ref.detectChanges();
  }

  public get GridSize() {
    return (this.containerDiv?.nativeElement as HTMLElement).getBoundingClientRect().height -
      16 - (this.controlsDiv?.nativeElement as HTMLElement).getBoundingClientRect().height;
  }

  public async pickerEnterKeyEvent() {
    const afterItemLoaded = () => {
      this.addFoundTests();
      this.cleanLookup();
      this.codeTextBox?.instance.reset();
    };
    this.textCodeChanged({value: this.codeTextBox?.text}, afterItemLoaded);
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public initResponsive() {
    this.isPhone = this.screen.sizes['screen-x-small'];
    this.screen.changed
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.isPhone = this.screen.sizes['screen-x-small'];
      });
  }

  private markNodeAsHidden(node: any) {
    for (let child of node.children) {
      this.markNodeAsHidden(child);
    }

    if (node.data && node.data.isConfidential) {
      node.visible = false;
    }
  }

  public nodesInitialized(nodes: any) {
    this.markNodeAsHidden(nodes.root);
  }
}
